import { Command } from 'commander';
import { checkbox, confirm, select } from '@inquirer/prompts';
import fs from 'node:fs/promises';
import path from 'node:path';
import { existsSync } from 'node:fs';
import { selectTheme, checkboxTheme } from '../../utils/prompt-theme.js';

// Performance Groups Definition
interface SettingsGroup {
    name: string;
    description: string;
    settings: Record<string, unknown>;
}

const PERFORMANCE_GROUPS: Record<string, SettingsGroup> = {
    telemetry: {
        name: 'Telemetry',
        description: 'Tắt tất cả telemetry và thu thập dữ liệu',
        settings: {
            "telemetry.telemetryLevel": "off",
            "telemetry.feedback.enabled": false,
            "workbench.enableExperiments": false,
            "workbench.settings.enableNaturalLanguageSearch": false,
        }
    },
    languageServers: {
        name: 'Language Servers',
        description: 'Tắt/giảm tải các language servers (tsserver, eslint...)',
        settings: {
            // TypeScript
            "typescript.tsserver.experimental.enableProjectDiagnostics": false,
            "typescript.disableAutomaticTypeAcquisition": true,
            "typescript.tsserver.maxTsServerMemory": 2048,
            "typescript.surveys.enabled": false,
            // JavaScript
            "js/ts.implicitProjectConfig.strictNullChecks": false,
            // ESLint
            "eslint.enable": false,
            // Prettier
            "prettier.enable": false,
            // CSS/HTML
            "css.validate": false,
            "html.validate.scripts": false,
            "html.validate.styles": false,
            // JSON
            "json.schemaDownload.enable": false,
        }
    },
    git: {
        name: 'Git Integration',
        description: 'Tắt Git tích hợp để cải thiện hiệu suất',
        settings: {
            "git.enabled": false,
            "git.autoRepositoryDetection": false,
            "git.decorations.enabled": false,
            "git.path": null,
            "scm.diffDecorations": "none",
        }
    },
    fileWatcher: {
        name: 'File Watcher',
        description: 'Loại trừ các thư mục khỏi file watcher',
        settings: {
            "files.watcherExclude": {
                "**/node_modules/**": true,
                "**/.git/**": true,
                "**/vendor/**": true,
                "**/dist/**": true,
                "**/build/**": true,
                "**/.next/**": true,
                "**/out/**": true,
                "**/.nuxt/**": true,
                "**/.cache/**": true,
                "**/coverage/**": true,
            },
            "files.exclude": {
                "**/.git": true,
                "**/.DS_Store": true,
                "**/node_modules": false, // Keep visible but don't watch
            }
        }
    },
    search: {
        name: 'Search',
        description: 'Loại trừ các thư mục khỏi search',
        settings: {
            "search.exclude": {
                "**/node_modules": true,
                "**/bower_components": true,
                "**/*.code-search": true,
                "**/dist": true,
                "**/build": true,
                "**/out": true,
                "**/.next": true,
                "**/.nuxt": true,
                "**/coverage": true,
                "**/.cache": true,
            },
            "search.followSymlinks": false,
        }
    },
    extensions: {
        name: 'Extensions',
        description: 'Tắt auto-update extensions',
        settings: {
            "extensions.autoUpdate": false,
            "extensions.autoCheckUpdates": false,
            "update.mode": "manual",
            "extensions.ignoreRecommendations": true,
        }
    },
    editorRendering: {
        name: 'Editor Rendering',
        description: 'Tối ưu rendering (minimap, whitespace, highlights...)',
        settings: {
            "editor.minimap.enabled": false,
            "editor.renderWhitespace": "none",
            "editor.renderControlCharacters": false,
            "editor.renderLineHighlight": "none",
            "editor.cursorBlinking": "solid",
            "editor.smoothScrolling": false,
            "editor.largeFileOptimizations": true,
            "editor.maxTokenizationLineLength": 2000,
            "editor.quickSuggestions": {
                "other": true,
                "comments": false,
                "strings": false
            },
            "editor.suggest.showStatusBar": false,
            "editor.hover.delay": 500,
            "editor.matchBrackets": "never",
        }
    },
    uiElements: {
        name: 'UI Elements',
        description: 'Ẩn các UI elements không cần thiết',
        settings: {
            "workbench.activityBar.visible": false,
            "workbench.statusBar.visible": false,
            "breadcrumbs.enabled": false,
            "window.menuBarVisibility": "compact",
            "workbench.editor.showTabs": true, // Keep tabs for navigation
            "workbench.startupEditor": "none",
        }
    }
};

export function createVSCodeCommand(): Command {
    const vscodeCommand = new Command('vscode')
        .description('Quản lý cài đặt VSCode cho dự án hiện tại');

    // Interactive mode (default)
    vscodeCommand.action(async () => {
        await interactiveMode();
    });

    // Enable subcommand
    vscodeCommand
        .command('enable')
        .description('Enable các nhóm tối ưu cụ thể')
        .argument('[groups...]', 'Tên các nhóm cần enable (telemetry, languageServers, git, ...)')
        .action(async (groups: string[]) => {
            if (groups.length === 0) {
                await selectGroupsToApply('enable');
            } else {
                await applyGroups(groups, 'enable');
            }
        });

    // Disable subcommand
    vscodeCommand
        .command('disable')
        .description('Disable các nhóm tối ưu cụ thể (restore về default)')
        .argument('[groups...]', 'Tên các nhóm cần disable')
        .action(async (groups: string[]) => {
            if (groups.length === 0) {
                await selectGroupsToApply('disable');
            } else {
                await applyGroups(groups, 'disable');
            }
        });

    // Max performance preset
    vscodeCommand
        .command('max-performance')
        .description('Enable tất cả các tối ưu để đạt hiệu suất tối đa')
        .action(async () => {
            const allGroups = Object.keys(PERFORMANCE_GROUPS);
            await applyGroups(allGroups, 'enable');
        });

    // Reset subcommand
    vscodeCommand
        .command('reset')
        .description('Reset về settings mặc định (xóa tất cả tối ưu)')
        .argument('[groups...]', 'Tên các nhóm cần reset (để trống = reset tất cả)')
        .action(async (groups: string[]) => {
            await resetSettings(groups);
        });

    // List subcommand
    vscodeCommand
        .command('list')
        .description('Liệt kê tất cả các nhóm tối ưu có sẵn')
        .action(() => {
            console.log('🔧  Các nhóm tối ưu có sẵn:\n');
            Object.entries(PERFORMANCE_GROUPS).forEach(([key, group]) => {
                console.log(`  ${key.padEnd(20)} - ${group.name}`);
                console.log(`  ${' '.repeat(20)}   ${group.description}`);
                console.log();
            });
        });

    return vscodeCommand;
}

async function interactiveMode() {
    console.log('🔧  Quản lý cài đặt VSCode\n');
    console.log('╭─────────────────────────────────────────────────────╮');
    console.log('│  📌 Hướng dẫn sử dụng:                              │');
    console.log('│     • Dùng phím ↑↓ để di chuyển                     │');
    console.log('│     • Nhấn SPACE để chọn/bỏ chọn nhóm               │');
    console.log('│     • Nhấn ENTER để xác nhận và áp dụng             │');
    console.log('│     • Nhấn Ctrl+C để hủy                            │');
    console.log('╰─────────────────────────────────────────────────────╯\n');

    const action = await select({
        message: 'Bạn muốn làm gì?',
        choices: [
            { name: '✅ Enable các nhóm tối ưu', value: 'enable' },
            { name: '❌ Disable các nhóm tối ưu', value: 'disable' },
            { name: '🚀 Max Performance (enable tất cả)', value: 'max' },
            { name: '🔄 Reset về mặc định', value: 'reset' },
        ],
        theme: selectTheme,
    });

    if (action === 'max') {
        const allGroups = Object.keys(PERFORMANCE_GROUPS);
        await applyGroups(allGroups, 'enable');
    } else if (action === 'reset') {
        await resetSettings([]);
    } else {
        await selectGroupsToApply(action as 'enable' | 'disable');
    }
}

async function selectGroupsToApply(action: 'enable' | 'disable') {
    const choices = Object.entries(PERFORMANCE_GROUPS).map(([key, group]) => ({
        name: `${group.name} - ${group.description}`,
        value: key,
    }));

    try {
        const selectedGroups = await checkbox({
            message: `Chọn các nhóm để ${action === 'enable' ? 'enable' : 'disable'} (SPACE để chọn, ENTER để xác nhận):`,
            choices,
            theme: checkboxTheme,
        });

        if (selectedGroups.length === 0) {
            console.log('\n⚠️  Bạn chưa chọn nhóm nào!');
            console.log('   💡 Mẹo: Hãy nhấn SPACE để chọn ít nhất 1 nhóm trước khi nhấn ENTER.');
            return;
        }

        await applyGroups(selectedGroups, action);
    } catch {
        console.log('\n❌ Đã hủy thao tác.');
    }
}

async function applyGroups(groupKeys: string[], action: 'enable' | 'disable') {
    const vscodeDir = path.join(process.cwd(), '.vscode');
    const settingsPath = path.join(vscodeDir, 'settings.json');

    // Validate group keys
    const invalidGroups = groupKeys.filter(key => !PERFORMANCE_GROUPS[key]);
    if (invalidGroups.length > 0) {
        console.log(`\n❌ Nhóm không hợp lệ: ${invalidGroups.join(', ')}`);
        console.log('   💡 Chạy "jai1 vscode list" để xem danh sách nhóm có sẵn.');
        return;
    }

    // Ensure .vscode directory exists
    if (!existsSync(vscodeDir)) {
        await fs.mkdir(vscodeDir, { recursive: true });
        console.log('📁 Đã tạo thư mục .vscode/');
    }

    // Read existing settings
    let currentSettings: Record<string, unknown> = {};
    if (existsSync(settingsPath)) {
        try {
            const content = await fs.readFile(settingsPath, 'utf-8');
            currentSettings = JSON.parse(content);
            console.log('📄 Đã đọc cài đặt hiện tại từ settings.json');
        } catch {
            console.warn('⚠️  Không thể đọc settings.json (có thể chứa comments).');
            const confirmOverwrite = await confirm({
                message: 'Ghi đè file settings.json hiện tại?',
                default: false
            });

            if (!confirmOverwrite) {
                console.log('❌ Đã hủy thao tác.');
                return;
            }
            currentSettings = {};
        }
    }

    // Apply or remove settings
    const newSettings = { ...currentSettings };

    console.log(`\n📝 Đang ${action === 'enable' ? 'enable' : 'disable'} các nhóm:\n`);

    for (const key of groupKeys) {
        const group = PERFORMANCE_GROUPS[key];
        console.log(`   ${action === 'enable' ? '✓' : '✗'} ${group.name}`);

        if (action === 'enable') {
            // Merge settings
            for (const [settingKey, settingValue] of Object.entries(group.settings)) {
                if (typeof settingValue === 'object' && settingValue !== null && !Array.isArray(settingValue) &&
                    typeof newSettings[settingKey] === 'object' && newSettings[settingKey] !== null) {
                    newSettings[settingKey] = {
                        ...(newSettings[settingKey] as Record<string, unknown>),
                        ...settingValue
                    };
                } else {
                    newSettings[settingKey] = settingValue;
                }
            }
        } else {
            // Remove settings (disable)
            for (const settingKey of Object.keys(group.settings)) {
                delete newSettings[settingKey];
            }
        }
    }

    // Write back
    await fs.writeFile(settingsPath, JSON.stringify(newSettings, null, 2));
    console.log(`\n✅ Đã cập nhật cài đặt VSCode tại: ${settingsPath}`);
    console.log('💡 Mẹo: Khởi động lại VSCode để áp dụng các thay đổi.');
}

async function resetSettings(groupKeys: string[]) {
    const vscodeDir = path.join(process.cwd(), '.vscode');
    const settingsPath = path.join(vscodeDir, 'settings.json');

    if (!existsSync(settingsPath)) {
        console.log('\n⚠️  Không tìm thấy file settings.json');
        return;
    }

    const confirmReset = await confirm({
        message: groupKeys.length === 0
            ? 'Reset TẤT CẢ settings về mặc định (xóa toàn bộ file)?'
            : `Reset các nhóm: ${groupKeys.join(', ')}?`,
        default: false
    });

    if (!confirmReset) {
        console.log('❌ Đã hủy thao tác.');
        return;
    }

    if (groupKeys.length === 0) {
        // Reset all - delete file
        await fs.unlink(settingsPath);
        console.log('\n✅ Đã xóa file settings.json');
    } else {
        // Reset specific groups
        await applyGroups(groupKeys, 'disable');
    }

    console.log('💡 Mẹo: Khởi động lại VSCode để áp dụng các thay đổi.');
}
